import React from "react";
import { LoadingSpinnerSize } from "../../types/common";

interface LoadingSpinnerProps {
  message?: string;
  size?: LoadingSpinnerSize;
  className?: string;
  variant?: "default" | "dots" | "pulse" | "gradient";
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = "Loading...",
  size = "medium",
  className = "",
  variant = "gradient",
}) => {
  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-8 h-8",
    large: "w-12 h-12",
  };

  const textSizeClasses = {
    small: "text-sm",
    medium: "text-base",
    large: "text-lg",
  };

  if (variant === "dots") {
    return (
      <div data-section="loading-spinner-root" className={`flex flex-col items-center justify-center gap-4 ${className}`}>
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`bg-blue-500 rounded-full animate-bounce ${
                size === "small" ? "w-2 h-2" : size === "large" ? "w-4 h-4" : "w-3 h-3"
              }`}
              style={{ animationDelay: `${i * 0.16}s` }}
            />
          ))}
        </div>
        {message && <p className={`font-medium text-gray-700 ${textSizeClasses[size]}`}>{message}</p>}
      </div>
    );
  }

  if (variant === "pulse") {
    return (
      <div data-section="loading-spinner-root" className={`flex flex-col items-center justify-center gap-4 ${className}`}>
        <div className={`bg-blue-500 rounded-full animate-pulse ${sizeClasses[size]}`} />
        {message && <p className={`font-medium text-gray-700 ${textSizeClasses[size]}`}>{message}</p>}
      </div>
    );
  }

  if (variant === "gradient") {
    return (
      <div data-section="loading-spinner-root" className={`flex flex-col items-center justify-center gap-6 ${className}`}>
        <div className="relative">
          {/* Outer ring */}
          <div className={`${sizeClasses[size]} rounded-full border-4 border-gray-200`} />
          {/* Gradient spinner */}
          <div
            className={`absolute inset-0 ${sizeClasses[size]} rounded-full border-4 border-transparent animate-spin`}
            style={{
              background: "conic-gradient(from 0deg, transparent, #3b82f6, #8b5cf6, transparent)",
              mask: "radial-gradient(circle at center, transparent 60%, white 60%)",
              WebkitMask: "radial-gradient(circle at center, transparent 60%, white 60%)",
            }}
          />
          {/* Inner glow */}
          <div
            className={`absolute inset-1 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full animate-pulse`}
          />
        </div>
        {message && (
          <div className="text-center">
            <p
              className={`font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent ${textSizeClasses[size]}`}
            >
              {message}
            </p>
            <div className="flex justify-center mt-2">
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default spinner
  return (
    <div data-section="loading-spinner-root" className={`flex flex-col items-center justify-center gap-4 ${className}`}>
      <div className={`border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin ${sizeClasses[size]}`} />
      {message && <p className={`font-medium text-gray-700 ${textSizeClasses[size]}`}>{message}</p>}
    </div>
  );
};

export default LoadingSpinner;
